/**
 * 私宴預約表單(藝境・私宴 →「預約私宴」→ 邀請卡 CTA 進來)
 *
 * 多場次支援(同一天只一場由後台把關,不同天可同時多場開放):
 *   - ?event=<id> 直達指定場次(邀請卡 CTA 帶);沒帶時 1 場自動選、多場先挑
 *   - 各場席位 / 我的預約狀態獨立
 * 姓名 / 手機從會員資料預填;送出後後端 push 確認卡到 LINE。
 * 席次滿了不擋 — 後端轉候補登記,UI 明示候補狀態。
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hourglass, Leaf, Mail } from "lucide-react";
import StatusMark from "../components/StatusMark";
import {
  getBanquetOpen,
  reserveBanquet,
  type BanquetOpenEvent,
  type BanquetStatus,
} from "../api/banquet";
import { getMe } from "../api/members";
import { useUIStore } from "../store/uiStore";

const PARTY_OPTIONS = [1, 2, 3, 4];

const STATUS_LABEL: Record<BanquetStatus, string> = {
  pending: "待確認",
  confirmed: "已確認",
  waitlist: "候補中",
  cancelled: "已取消",
};

export default function BanquetReserve() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramEventId = searchParams.get("event") || "";

  const [events, setEvents] = useState<BanquetOpenEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | { waitlist: boolean }>(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([getBanquetOpen(), getMe()]);
        setEvents(r.events);
        setName(m.name || m.display_name || "");
        setPhone(m.phone || "");
        // 選場次:?event= 指定且存在 → 它;只有一場 → 自動選;多場 → 留空讓 user 挑
        if (paramEventId && r.events.some((e) => e.event_id === paramEventId)) {
          setSelectedId(paramEventId);
        } else if (r.events.length === 1) {
          setSelectedId(r.events[0].event_id);
        }
      } catch {
        useUIStore.getState().showToast("載入失敗,請稍候再試", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const event = events.find((e) => e.event_id === selectedId) || null;

  async function submit() {
    if (!event) return;
    if (!name.trim() || !phone.trim()) {
      useUIStore.getState().showToast("姓名 / 手機必填", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await reserveBanquet({
        event_id: event.event_id,
        name,
        phone,
        party_size: partySize,
        note,
      });
      if (r.status === "ok") {
        setDone({ waitlist: !!r.waitlist });
      } else if (r.status === "already_reserved" && r.reservation) {
        setEvents((prev) =>
          prev.map((e) =>
            e.event_id === event.event_id ? { ...e, my_reservation: r.reservation! } : e,
          ),
        );
      } else if (r.status === "event_closed") {
        useUIStore.getState().showToast("本場次報名已截止", "error");
      } else {
        useUIStore.getState().showToast(`送出失敗:${r.status}`, "error");
      }
    } catch {
      useUIStore.getState().showToast("送出失敗,請稍候再試", "error");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-muted">
        載入中…
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6">
        <StatusMark icon={Leaf} />
        <div className="mt-3 text-ink">目前沒有開放預約的場次</div>
        <div className="mt-2 text-sm text-muted text-center">
          私宴與品鑑沙龍採不定期舉辦,開放時將於「藝境・私宴」公告。
        </div>
        <button onClick={() => navigate("/home")} className="btn-primary mt-6 px-8">
          返回首頁
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3">
        <button
          onClick={() => (event && events.length > 1 ? setSelectedId("") : navigate("/home"))}
          className="text-white text-xl"
        >
          ‹
        </button>
        <div>
          <h1 className="text-xl font-bold">預約私宴</h1>
          <div className="text-xs opacity-80">Reserve Your Seat</div>
        </div>
      </header>

      {!event ? (
        /* 多場開放中 → 先選場次 */
        <div className="px-5 mt-5 space-y-3">
          <div className="text-sm text-muted">目前有 {events.length} 場開放預約,請選擇場次:</div>
          {events.map((e) => (
            <button
              key={e.event_id}
              onClick={() => setSelectedId(e.event_id)}
              className="card w-full text-left flex items-center gap-3"
            >
              {e.hero_url ? (
                <img src={e.hero_url} alt="" className="w-14 h-20 object-cover rounded shrink-0" />
              ) : (
                <div className="w-14 h-20 bg-line rounded shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-ink">{e.title}</div>
                <div className="text-sm text-muted">{e.date_label} {e.time_label}</div>
                <div className="text-xs text-muted">
                  {e.venue}
                  {e.my_reservation
                    ? ` · 已預約(${STATUS_LABEL[e.my_reservation.status]})`
                    : e.remaining <= 0
                      ? " · 已滿(可候補)"
                      : ""}
                </div>
              </div>
              <span className="text-primary">›</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* 場次資訊 */}
          <div className="px-5 mt-5">
            <div className="card overflow-hidden p-0">
              {/* 海報用原始比例完整呈現,不裁切;沒傳海報就不顯示 */}
              {event.hero_url && <img src={event.hero_url} alt={event.title} className="w-full" />}
              <div className="p-5 space-y-3">
                <div>
                  <div className="text-lg font-bold text-ink">{event.title}</div>
                  {event.title_en && (
                    <div className="text-xs text-muted tracking-wide">{event.title_en.toUpperCase()}</div>
                  )}
                </div>
                {event.tagline && (
                  <div className="text-primary font-medium text-center border-y border-line py-2">
                    {event.tagline}
                  </div>
                )}
                <dl className="space-y-1.5 text-sm">
                  {event.date_label && <div className="flex gap-3"><dt className="text-muted w-12 shrink-0">日期</dt><dd className="text-ink">{event.date_label}</dd></div>}
                  {event.time_label && <div className="flex gap-3"><dt className="text-muted w-12 shrink-0">時間</dt><dd className="text-ink">{event.time_label}</dd></div>}
                  {event.venue && <div className="flex gap-3"><dt className="text-muted w-12 shrink-0">地點</dt><dd className="text-ink">{event.venue}<br /><span className="text-muted text-xs">{event.venue_address}</span></dd></div>}
                  {event.fee_label && <div className="flex gap-3"><dt className="text-muted w-12 shrink-0">費用</dt><dd className="text-ink">{event.fee_label}</dd></div>}
                  {event.capacity_label && <div className="flex gap-3"><dt className="text-muted w-12 shrink-0">席次</dt><dd className="text-ink">{event.capacity_label}{event.remaining <= 0 ? "(本場次已滿,可登記候補)" : ""}</dd></div>}
                </dl>
              </div>
            </div>
          </div>

          <div className="px-5 mt-5">
            {done ? (
              /* 送出成功 */
              <div className="card text-center space-y-3 py-8">
                <StatusMark icon={done.waitlist ? Hourglass : Mail} />
                <div className="text-lg font-bold text-ink">
                  {done.waitlist ? "已為您列入候補" : "預約已收到"}
                </div>
                <div className="text-sm text-muted px-4">
                  {done.waitlist
                    ? "本場次席次已滿,若有席次釋出,專屬顧問將第一時間與您聯繫。"
                    : "專屬顧問將儘速與您聯繫,確認席次與品鑑費用事宜。確認卡已送至您的 LINE。"}
                </div>
                <button onClick={() => navigate("/home")} className="btn-primary px-8 mt-2">
                  返回首頁
                </button>
              </div>
            ) : event.my_reservation ? (
              /* 已有預約 */
              <div className="card text-center space-y-3 py-8">
                <StatusMark icon={Mail} />
                <div className="text-lg font-bold text-ink">您已完成預約</div>
                <div className="text-sm text-ink">
                  {event.my_reservation.name} · {event.my_reservation.party_size} 位 · 狀態:
                  {STATUS_LABEL[event.my_reservation.status]}
                </div>
                <div className="text-sm text-muted px-4">
                  如需調整人數或取消,專屬顧問聯繫時告知即可。
                </div>
                <button onClick={() => navigate("/home")} className="btn-primary px-8 mt-2">
                  返回首頁
                </button>
              </div>
            ) : (
              /* 預約表單 */
              <div className="card space-y-4">
                <div className="text-sm text-muted">
                  席次珍稀,採全預約制。送出後由專屬顧問與您聯繫確認。
                </div>
                <div>
                  <label className="label">姓名 *</label>
                  <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <label className="label">手機 *</label>
                  <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div>
                  <label className="label">出席人數 *</label>
                  <select
                    className="input"
                    value={partySize}
                    onChange={(e) => setPartySize(Number(e.target.value))}
                  >
                    {PARTY_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} 位</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">備註(忌口、攜伴資訊等,選填)</label>
                  <textarea
                    className="input min-h-20"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="例:1 位素食 / 攜母親同行"
                  />
                </div>
                <button onClick={submit} disabled={busy} className="btn-primary w-full">
                  {busy ? "送出中…" : event.remaining <= 0 ? "登記候補" : "送出預約"}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
